import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    input,
    signal,
    viewChildren,
    ViewEncapsulation,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { S25CheckboxModule } from "../../../modules/s25-checkbox/s25.checkbox.module";
import { S25IconComponent } from "../../../modules/s25-icon/s25.icon.component";
import { S25LoadingInlineStaticComponent } from "../../../modules/s25-loading/s25.loading.inline.static.component";
import { Layout } from "../../../modules/s25-object-search/s25.object.search.util";
import { Item } from "../../../pojo/Item";
import { UserprefService } from "../../../services/userpref.service";
import { FlsStore } from "../../../stores/fls.store";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { s25Resource } from "../../../util/s25.resource";
import { S25Util } from "../../../util/s25-util";
import { EventFormObjectOccurrenceTableRowComponent } from "./event.form.object.occurrence.table.row.component";
import type { EventForm } from "..";
import { Nullable } from "../../../pojo/Util";

@TypeManagerDecorator("s25-ng-event-form-object-occurrences-table")
@Component({
    selector: "s25-ng-event-form-object-occurrences-table",
    standalone: true,
    imports: [
        FormsModule,
        S25CheckboxModule,
        S25IconComponent,
        S25LoadingInlineStaticComponent,
        EventFormObjectOccurrenceTableRowComponent,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    styles: `
        .ngTable {
            border-right: none;
            border-bottom: none;
        }
    `,
    template: `
        @if (!ready()) {
            <s25-ng-loading-inline-static />
        } @else if (objOccurrences().length) {
            <table class="ngTable rose-object-occurrences-table rose-added-object-table">
                <thead class="ngTableHeader">
                    <tr class="ngTableRow">
                        <th class="ngTableCell">Date</th>
                        <th class="ngTableCell">Time</th>
                        <th class="ngTableCell">Issues</th>
                        @if (!inline()) {
                            <th class="ngTableCell">Included</th>
                        }
                        @if (isLocation() && canShare()) {
                            <th class="ngTableCell">
                                <span class="rose-object-occurrences-table__sharedHeader">
                                    Shared
                                    <span
                                        class="c-tooltip__container rose-object-occurrences-table__sharedInfo"
                                        role="button"
                                        tabindex="0"
                                        aria-label="More about Shared"
                                        aria-describedby="shared-th-tooltip-occ-form"
                                    >
                                        <span class="rose-object-occurrences-table__sharedInfoIcon" aria-hidden="true">
                                            <s25-ng-icon [type]="'info'" [ariaLabel]="false" [size]="24" />
                                        </span>
                                        <div role="tooltip" class="c-tooltip" id="shared-th-tooltip-occ-form">
                                            <span class="c-tooltip__content"
                                                >When checked, this location can be double-booked: other events may use
                                                the same space at the same time. When unchecked, the space is reserved
                                                for this event only.</span
                                            >
                                        </div>
                                    </span>
                                </span>
                            </th>
                        }
                        @if (isLocation() && hasLayouts()) {
                            <th class="ngTableCell">Layout</th>
                        }
                        <th class="ngTableCell">Instructions</th>
                        @if (isLocation()) {
                            <th class="ngTableCell">Attendance</th>
                        }
                        @if (isResource()) {
                            <th class="ngTableCell">Quantity</th>
                            <th class="ngTableCell">Available</th>
                        }
                    </tr>
                </thead>
                <tbody class="ngTableBody">
                    @if (!inline()) {
                        <tr class="ngTableRow forAllRow">
                            <td class="ngTableCell ngBold" aria-label="Date" tabindex="0">Set For All</td>
                            <td class="ngTableCell" aria-hidden="true"></td>
                            <td class="ngTableCell" aria-hidden="true"></td>
                            <td class="ngTableCell c-text-align--center" data-label="Included">
                                <s25-ng-checkbox
                                    [value]="isIncluded()"
                                    (valueChange)="onIncludedChange($event)"
                                    [ariaLabel]="'Included'"
                                    [noLabel]="true"
                                    [labelId]="'all-included'"
                                />
                            </td>
                            @if (isLocation() && canShare()) {
                                <td class="ngTableCell c-text-align--center" data-label="Shared">
                                    <s25-ng-checkbox
                                        [value]="spaceShare()"
                                        (valueChange)="onShareChange($event)"
                                        [ariaLabel]="'Shared'"
                                        [noLabel]="true"
                                        [labelId]="'all-shared'"
                                        [disabled]="alwaysShared()"
                                    />
                                </td>
                            }
                            @if (isLocation() && hasLayouts()) {
                                <td class="ngTableCell" data-label="Layout">
                                    <select
                                        class="cn-form__control"
                                        aria-label="Layout"
                                        [ngModel]="layout()"
                                        [compareWith]="isSameLayout"
                                        (ngModelChange)="onLayoutChange($event)"
                                    >
                                        @for (itemLayout of layouts(); track itemLayout.layout_id) {
                                            <option [ngValue]="itemLayout">{{ layoutLabel(itemLayout) }}</option>
                                        }
                                    </select>
                                </td>
                            }
                            <td class="ngTableCell" data-label="Instructions">
                                <textarea
                                    rows="1"
                                    class="cn-form__control"
                                    aria-label="Instructions"
                                    [ngModel]="instructions()"
                                    (ngModelChange)="onInstructionsChange($event)"
                                ></textarea>
                            </td>
                            @if (isLocation()) {
                                <td class="ngTableCell" data-label="Attendance">
                                    <div class="numeric-wrapper">
                                        <input
                                            type="number"
                                            class="c-numeric-input"
                                            min="0"
                                            max="65536"
                                            aria-label="Attendance"
                                            [ngModel]="spaceAttendance()"
                                            (ngModelChange)="onAttendanceChange($event)"
                                        />
                                    </div>
                                </td>
                            }
                            @if (isResource()) {
                                <td class="ngTableCell" data-label="Quantity">
                                    <div class="c-displayInlineBlock numeric-wrapper">
                                        <input
                                            type="text"
                                            inputmode="numeric"
                                            class="c-input c-numeric-input"
                                            aria-label="Quantity"
                                            [ngModel]="resourceQuantity()"
                                            (ngModelChange)="onQuantityChange($event)"
                                        />
                                    </div>
                                </td>
                                <td class="ngTableCell"></td>
                            }
                        </tr>
                    }
                    @for (objOcc of objOccurrences(); track objOcc.occ.uuid) {
                        <tr
                            s25-ng-event-form-object-occurrence-row
                            [objOcc]="objOcc"
                            [inline]="inline()"
                            [canShare]="canShare()"
                            [layouts]="layouts()"
                            [alwaysShared]="alwaysShared()"
                            [dateFormat]="dateFormat.value() ?? ''"
                            [timeFormat]="timeFormat.value() ?? ''"
                            [dateTimeFormat]="dateTimeFormat.value() ?? ''"
                        ></tr>
                    }
                </tbody>
            </table>
        }
    `,
})
export class EventFormObjectOccurrencesTableComponent {
    readonly objOccurrences = input<EventForm.ObjectOccurrence[]>([]);
    readonly inline = input(false, { transform: (value: unknown) => S25Util.toBool(value) });
    /** Pushed from AngJS when item.layouts is replaced after the location web service responds. */
    readonly layouts = input([], {
        transform: (layouts: Nullable<Layout[]>) => layouts ?? [],
    });
    readonly alwaysShared = input(false, { transform: (value: unknown) => S25Util.toBool(value) });

    private readonly fls = inject(FlsStore);
    private readonly rows = viewChildren(EventFormObjectOccurrenceTableRowComponent);

    readonly dateFormat = s25Resource(() => UserprefService.getS25Dateformat());
    readonly timeFormat = s25Resource(() => UserprefService.getS25Timeformat());
    readonly dateTimeFormat = s25Resource(() => UserprefService.getS25DateTimeformat());

    readonly isSameLayout = Layout.isSame;
    readonly layoutLabel = Layout.label;

    readonly canShare = computed(() => this.fls.loaded() && this.fls.hasFull("EVENT_SHARE"));
    readonly firstOccurrence = computed(() => this.objOccurrences()[0]);
    readonly itemTypeId = computed(() => this.firstOccurrence()?.item.itemTypeId);
    readonly isLocation = computed(() => this.itemTypeId() === Item.Ids.Location);
    readonly isResource = computed(() => this.itemTypeId() === Item.Ids.Resource);
    readonly hasLayouts = computed(() => this.layouts().length > 0);
    /** FLS only decides whether the Shared column shows, so it must not hold the whole table back. */
    readonly ready = computed(
        () => !!this.dateFormat.value() && !!this.timeFormat.value() && !!this.dateTimeFormat.value(),
    );

    readonly isIncluded = signal(false);
    readonly spaceShare = signal(false);
    readonly layout = signal<Layout | undefined>(undefined);
    readonly instructions = signal("");
    readonly spaceAttendance = signal<number | string>("");
    readonly resourceQuantity = signal<number | string>("");

    onIncludedChange(isIncluded: boolean): void {
        this.isIncluded.set(isIncluded);
        this.rows().forEach((row) => row.applyInclude(isIncluded));
    }

    onShareChange(spaceShare: boolean): void {
        this.spaceShare.set(spaceShare);
        this.rows().forEach((row) => row.applyShare(spaceShare));
    }

    onInstructionsChange(instructions: string): void {
        this.instructions.set(instructions);
        this.rows().forEach((row) => row.applyInstructions(instructions));
    }

    onAttendanceChange(spaceAttendance: number | string): void {
        this.spaceAttendance.set(spaceAttendance);
        const parsed = parseInt(String(spaceAttendance), 10);
        if (!Number.isFinite(parsed)) return;
        this.rows().forEach((row) => row.applyAttendance(parsed));
    }

    onQuantityChange(resourceQuantity: number | string): void {
        if (resourceQuantity === this.resourceQuantity()) return;
        this.resourceQuantity.set(resourceQuantity);
        const parsed = parseInt(String(resourceQuantity), 10);
        if (!Number.isFinite(parsed)) return;
        this.rows().forEach((row) => row.applyQuantity(parsed));
        this.rows().forEach((row) => row.refreshExceedingQuantity());
    }

    onLayoutChange(layout: Layout): void {
        this.layout.set(layout);
        const layoutId = layout.layout_id;
        if (layoutId == null) return;
        this.rows().forEach((row) => row.applyLayout(layoutId));
    }
}
